// Frontend API caller for /api/subscription — subscription management

import type { Result } from '@shared/types/result.ts';
import { ok, err } from '@shared/types/result.ts';

export type SubscriptionStatus = Readonly<{
  readonly subscribed: boolean;
  readonly subscription: Readonly<{
    readonly id: string;
    readonly wallet_address: string;
    readonly chain_network_id: string;
    readonly is_active: number;
    readonly user_cancelled: number;
    readonly current_amount_cents: number;
    readonly next_renewal_at: number;
    readonly last_renewed_at: number | null;
    readonly created_at: number;
  }> | null;
  readonly features: ReadonlyArray<Readonly<{
    readonly id: string;
    readonly subscription_id: string;
    readonly feature_type: string;
    readonly price_cents: number;
    readonly enabled_at: number;
    readonly disabled_at: number | null;
  }>>;
  readonly userCancelled?: boolean;
}>;

export type CreateSubscriptionResponse = Readonly<{
  readonly subscriptionId: string;
  readonly featureId: string;
  readonly chainNetworkId: string;
  readonly proratedAmountCents: number;
  readonly fullMonthlyAmountCents: number;
  readonly weekNumber: number;
  readonly isFreeUntilFirst: boolean;
  readonly nextRenewalAt: number;
}>;

export type ProrationResponse = Readonly<{
  readonly amountCents: number;
  readonly weekNumber: number;
  readonly isFree: boolean;
  readonly fullMonthlyAmountCents: number;
}>;

const _fetchApi = async <T>(
  path: string,
  sessionId: string | null,
  options?: RequestInit,
): Promise<Result<T>> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (sessionId) headers['Authorization'] = `Bearer ${sessionId}`;

    const response = await fetch(`/api/subscription${path}`, { ...options, headers });

    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: response.statusText }));
      return err(typeof body.error === 'string' ? body.error : `Request failed (${response.status})`);
    }

    const json = await response.json();
    return ok(Object.freeze(json.data as T));
  } catch (e) {
    return err(`Network error: ${e instanceof Error ? e.message : String(e)}`);
  }
};

export const fetchSubscriptionStatus = (sessionId: string): Promise<Result<SubscriptionStatus>> =>
  _fetchApi<SubscriptionStatus>('/', sessionId);

export const createSubscription = (
  sessionId: string,
  chainNetworkId: string,
  featureType: string,
): Promise<Result<CreateSubscriptionResponse>> =>
  _fetchApi<CreateSubscriptionResponse>('/', sessionId, {
    method: 'POST',
    body: JSON.stringify({ chainNetworkId, featureType }),
  });

export const cancelSubscription = (
  sessionId: string,
): Promise<Result<{ cancelled: boolean; activeUntil: number }>> =>
  _fetchApi('/cancel', sessionId, { method: 'POST' });

export const reactivateSubscription = (
  sessionId: string,
): Promise<Result<{ reactivated: boolean; nextRenewalAt: number; proratedAmountCents?: number }>> =>
  _fetchApi('/reactivate', sessionId, { method: 'POST' });

export const fetchProration = (): Promise<Result<ProrationResponse>> =>
  _fetchApi<ProrationResponse>('/proration', null);
